import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner } from '@/components/ui';
import { customerBillingService, type CustomerBillingInvoice } from '@/api/services/customerBilling';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export default function BillingInvoicesPage() {
  const { me } = useMe();
  const canView = me?.permissions.canViewCustomerBilling ?? false;
  const [items, setItems] = useState<CustomerBillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    customerBillingService
      .invoices()
      .then((res) => setItems(res.items))
      .catch((e) => setError(formatApiError(e, 'Failed to load invoices')))
      .finally(() => setLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <>
        <PageHeader title="Invoices" subtitle="Invoices and receipts for your account" />
        <p className="muted">You do not have permission to view invoices.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Invoices" subtitle="Invoices and receipts for your account">
        <Link to="/billing" className="btn btn-ghost btn-sm">
          Back to billing
        </Link>
      </PageHeader>
      {error ? <ErrorBanner message={error} /> : null}
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && items.length === 0 ? <p className="muted">No invoices yet.</p> : null}
      <div className="table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Links</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</td>
                <td>{item.description || item.id}</td>
                <td>{formatMoney(item.amountCents, item.currency)}</td>
                <td>{item.status}</td>
                <td>
                  {item.hostedInvoiceUrl ? (
                    <a href={item.hostedInvoiceUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      View
                    </a>
                  ) : null}
                  {item.invoicePdf ? (
                    <a href={item.invoicePdf} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      PDF
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
